import { Loader2 } from "lucide-react";

function ClinicMark({ clinic }) {
  if (clinic?.logo_url) {
    return <img src={clinic.logo_url} alt={clinic.clinic_name || "Clinic logo"} className="h-10 max-w-[220px] object-contain" data-testid="public-checkin-logo" />;
  }
  return (
    <span className="font-display text-lg font-bold tracking-tight" data-testid="public-checkin-clinic">
      {clinic?.clinic_name || "Your clinic"}
    </span>
  );
}

// Calm, high-contrast frame for every client-facing progress review page.
export function PublicShell({ clinic, children, testId, banner = null }) {
  const brand = clinic?.brand_color || "#18181b";
  const vars = { "--pr-brand": brand, "--pr-brand-fg": clinic?.brand_text || "#ffffff" };
  return (
    <div className="min-h-screen bg-[#f6f5f1] text-zinc-900" data-testid={testId} style={vars}>
      {banner}
      <header className="border-b border-zinc-200 bg-white" style={{ borderTop: `4px solid ${brand}` }} data-testid="public-checkin-header">
        <div className="mx-auto flex max-w-xl items-center justify-between gap-4 px-5 py-4">
          <ClinicMark clinic={clinic} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Exercise progress review</span>
        </div>
      </header>
      <main className="mx-auto max-w-xl px-5 py-8 sm:py-12">{children}</main>
      <footer className="mx-auto max-w-xl px-5 pb-10 text-sm leading-relaxed text-zinc-600">
        This progress review is not an emergency service. If you have urgent or severe symptoms, call 000 or seek medical help.
        {(clinic?.contact_phone || clinic?.contact_email) && (
          <span className="block mt-2" data-testid="public-checkin-contact">
            Contact your clinic: {[clinic.contact_phone, clinic.contact_email].filter(Boolean).join(" · ")}
          </span>
        )}
      </footer>
    </div>
  );
}

export function PublicLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6f5f1]" data-testid="public-checkin-loading">
      <Loader2 className="h-8 w-8 animate-spin text-zinc-500" aria-label="Loading" />
    </div>
  );
}

export function PublicMessage({ title, children, testId }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 sm:p-8" data-testid={testId} role="status">
      <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950">{title}</h1>
      <div className="mt-3 space-y-3 text-lg leading-relaxed text-zinc-700">{children}</div>
    </div>
  );
}

export function BigButton({ children, variant = "primary", className = "", ...rest }) {
  const styles = variant === "primary"
    ? "bg-[var(--pr-brand,#18181b)] text-[var(--pr-brand-fg,#ffffff)] hover:opacity-90 disabled:bg-zinc-400 disabled:opacity-100"
    : "border-2 border-zinc-300 bg-white text-zinc-900 hover:border-zinc-500";
  return (
    <button
      type="button"
      className={`inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl px-6 text-lg font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 ${styles} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function UrgentNotice({ text, testId = "public-checkin-urgent" }) {
  if (!text) return null;
  return (
    <div className="rounded-xl border-2 border-red-700 bg-red-50 p-4 text-base font-semibold leading-relaxed text-red-900" role="alert" data-testid={testId}>
      {text}
    </div>
  );
}
